<!-- TODO: simplify usage. E.g. maybe: determine default style based on 'is' -->
<!-- TODO: vite crashes during hot-updating when editing this file and a rs-checkbox is displayed -->

<!--
  A styled button-component that can either act as:

  * <button>      (default)
  * <a>           (if attribute 'href' is defined)
  * <router-link> (if attribute 'to' is defined)
  * <label>       (if attribute 'for' is defined)

  Note, the usage as <label> is discouraged and makes only sense if the <label>
  actually functions as a button for the user, e.g. in SearchFilterCheckbox.
-->

<template>
  <component
    :is="is"
    class="inline-flex"
    :class="[
      bgClass(),
      'expand-' + props.expand,
      disabled ? 'cursor-default' : 'cursor-' + cursor,
      fill === 'link' ? '' : 'rounded',
    ]"
    :target="target"
  >
    <span
      :class="[
        'size-' + props.size,
        fill === 'solid' && (variant === 'default' || disabled) && !focus
          ? 'bg-rs-content/70'
          : '',
        fill === 'link' ? '' : 'rounded',
        fill === 'link' && !disabled && !focus ? 'hover:opacity-60' : '',
        fill === 'link' && !noUnderline ? 'underline' : '',
        fill === 'solid' || fill === 'outline'
          ? 'border border-rs-' + (disabled && !focus ? 'neutral' : variant)
          : '',
        bgClassHover(),
        textClass(),
        textClassHover(),
        'justify-' + justify,
        allowWrap || flow ? '' : 'whitespace-nowrap',
        flow ? 'text-left' : 'w-full inline-flex items-center',
        overlayClass,
      ]"
    >
      <!-- TODO also for fill=clear? -->
      <IconOpenInNew
        class="inline w-4 h-4 mr-0.5"
        v-if="
          (is === 'a' || is === 'router-link') &&
          fill === 'link' &&
          target === '_blank'
        "
      />
      <slot></slot>
      <span
        class="inline-flex w-1 h-full"
        v-if="
          (is === 'a' || is === 'router-link') &&
          (fill === 'solid' || fill === 'outline') &&
          target === '_blank'
        "
      >
        <!-- TODO these margins do not scale well with button sizes -->
        <IconArrowOutward
          class="absolute -mt-[6px] -ml-[1px] h-4 w-4 text-rs-content/50"
        />
      </span>
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs } from "vue";
import IconOpenInNew from "../icons/IconOpenInNew.vue";
import IconArrowOutward from "../icons/IconArrowOutward.vue";
import useConfig from "@/composables/config";
import type { Variant } from "@/models/common.ts";

const attrs = useAttrs();
const { config } = useConfig();

const is = computed(() => {
  const is = attrs["for"]
    ? "label"
    : attrs["href"]
      ? "a"
      : attrs["to"]
        ? "router-link"
        : "button";
  if (is !== "button" && attrs["disabled"]) {
    return "span";
  }
  return is;
});

const disabled = computed(() => {
  return attrs["disabled"];
});

type fill = "clear" | "link" | "outline" | "solid";
export type Size =
  | "link"
  | "icon"
  | "small"
  | "medium-small"
  | "medium"
  | "large"
  | "xl";
type expand = "full" | "block";

const props = withDefaults(
  defineProps<{
    variant?: Variant;
    fill?: fill;
    size?: Size;
    expand?: expand;
    focus?: boolean;
    focusVariant?: Variant;
    justify?: string;
    cursor?: string;
    allowWrap?: boolean;
    flow?: boolean;
    noUnderline?: boolean;
    overlayClass?: string;
  }>(),
  {
    variant: "default",
    fill: "solid",
    size: "medium",
    justify: "center",
    cursor: "pointer",
  }
);

const target = ref<string>(attrs["target"] as string);

// set all external links to targe=_blank
if (attrs["href"]) {
  const hrefCheck = new URL(attrs["href"] as string, config.freidokBaseUrl)
    .href;
  if (
    hrefCheck &&
    (!hrefCheck.startsWith(config.freidokBaseUrl + "/") ||
      hrefCheck.toLowerCase().endsWith(".pdf"))
  ) {
    target.value = "_blank";
  }
}

const variant = computed(() => {
  return props.focus && props.focusVariant ? props.focusVariant : props.variant;
});

const bgClass = () => {
  if (props.focus) {
    return "bg-rs-" + variant.value;
  }
  if (props.fill === "solid") {
    return "bg-rs-" + (disabled.value ? "default" : variant.value);
  }
  return "";
};

const bgClassHover = () => {
  if (disabled.value) {
    return "";
  }
  if (props.fill === "solid" || props.focus) {
    return variant.value === "default"
      ? "hover:bg-white/30"
      : "hover:bg-black/20";
  }
  if (props.fill === "outline" || props.fill === "clear") {
    return "hover:bg-rs-" + variant.value;
  }
  return "";
};

const textClassContrast = () => {
  if (variant.value === "default") {
    return "";
  }
  if (variant.value === "warning") {
    return "text-rs-content";
  }
  return "text-white";
};

const textClass = () => {
  if (props.focus) {
    return textClassContrast();
  }
  if (disabled.value) {
    return "text-rs-content/50";
  }
  if (props.fill === "solid") {
    return textClassContrast();
  }
  if (props.fill === "outline" && variant.value === "warning") {
    return "text-rs-content";
  }
  return variant.value != "default" ? "text-rs-" + variant.value : "";
};

const textClassHover = () => {
  if ((props.fill === "outline" || props.fill === "clear") && !disabled.value) {
    return "hover:" + textClassContrast();
  }
  return "";
};
</script>

<style scoped>
.expand-full {
  @apply block mx-2;
}
.expand-block {
  @apply block mx-2;
}

.size-link {
}
.size-icon {
  @apply p-0.5 text-xs;
}
.size-small {
  @apply px-2  py-1  text-xs;
}
.size-medium-small {
  @apply px-1  py-0.5  text-sm;
}
.size-medium {
  @apply px-2  py-1  text-sm;
}
.size-large {
  @apply px-4  py-1  text-lg;
}
.size-xl {
  @apply px-6  py-2  text-xl;
}

.dear-tailwind-please-compile {
  @apply bg-rs-default
        bg-rs-confirm
        bg-rs-success
        bg-rs-warning
        bg-rs-danger
        bg-rs-highlight
        text-black
        hover:bg-rs-default
        hover:bg-rs-confirm
        hover:bg-rs-success
        hover:bg-rs-warning
        hover:bg-rs-danger
        hover:bg-rs-highlight
        border-rs-default
        border-rs-confirm
        border-rs-success
        border-rs-warning
        border-rs-danger
        border-rs-highlight
        text-rs-default
        text-rs-confirm
        text-rs-success
        text-rs-warning
        text-rs-danger
        text-rs-highlight
        hover:text-black
        hover:text-white
        hover:text-rs-content
        p-0;
}
</style>
